import React, { useState } from 'react';
import { useIsMutating } from '@tanstack/react-query';
import { EditingViewState, USER_VIEW_SETTINGS_KEY, UpdateViewRequest } from '@cs-cms/types';
import { StorageHelper } from '@cs-cms/utils';
import { useCurrentUserContext } from '../contexts/CurrentUserContext';
import { useGlobalizationContext } from '../contexts/GlobalizationContext';
import { initEditingViewState, ViewsContext } from '../contexts/ViewsContext';
import { useTranslation } from '../hooks/globalization/useTranslation';
import { useNotificationContext } from '../hooks/useNotificationContext';
import { useGetView } from '../hooks/view/useGetView';
import { useGetViews } from '../hooks/view/useGetViews';
import { useUpdateView } from '../hooks/view/useUpdateView';
import { mutateViewsKeys } from '../mutationKeys/view';

interface ViewsProviderProps {
  children?: React.ReactNode;
}

export const ViewsProvider: React.FC<ViewsProviderProps> = ({ children }) => {
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);
  const { selectedIndex } = useCurrentUserContext();
  const { data: views } = useGetViews(selectedIndex?.id);
  const [selectedView, setSelectedView] = useState<number | undefined>(undefined);
  const { data: currentViewDetails } = useGetView(selectedView, selectedLocale);
  const [editingViewStateOverrides, setEditingViewStateOverrides] = useState<EditingViewState | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const [overridesViewId, setOverridesViewId] = useState<number | undefined>(undefined);

  const editingViewState: EditingViewState =
    overridesViewId !== selectedView
      ? !currentViewDetails
        ? initEditingViewState
        : {
            fields: currentViewDetails.fields.map(field => ({
              id: field.id,
              name: field.displayName || field.name,
              width: field.width,
              facetable: field.facetable,
              facetsCount: field.facetsCount,
              isDocumentLink: field.isDocumentLink
            })),
            globalFilters: currentViewDetails.globalFilters
          }
      : (editingViewStateOverrides ??
        (!currentViewDetails
          ? initEditingViewState
          : {
              fields: currentViewDetails.fields.map(field => ({
                id: field.id,
                name: field.displayName || field.name,
                width: field.width,
                facetable: field.facetable,
                facetsCount: field.facetsCount,
                isDocumentLink: field.isDocumentLink
              })),
              globalFilters: currentViewDetails.globalFilters
            }));

  const setEditingViewState = (newState: EditingViewState) => {
    setEditingViewStateOverrides(newState);
    setOverridesViewId(selectedView);
    setHasUnsavedChanges(true);
  };

  const getStoredViewSelections = (): Record<number, number> => {
    const stored = StorageHelper.get(USER_VIEW_SETTINGS_KEY);
    return stored ? JSON.parse(stored) : {};
  };

  const storeViewSelection = (indexId: number, viewId: number) => {
    const stored = getStoredViewSelections();
    stored[indexId] = viewId;
    StorageHelper.set(USER_VIEW_SETTINGS_KEY, JSON.stringify(stored));
  };

  React.useEffect(() => {
    setSelectedView(undefined);
  }, [selectedIndex?.id]);

  React.useEffect(() => {
    if (!views?.length || selectedIndex?.id === undefined) {
      return;
    }

    const isSelectedViewValid = selectedView != null && views.some(view => view.id === selectedView);

    if (isSelectedViewValid) {
      return;
    }

    const storedViewSelections = getStoredViewSelections();
    const storedViewId = storedViewSelections[selectedIndex.id];

    if (storedViewId != null) {
      const foundView = views.find(view => view.id === storedViewId);
      if (foundView) {
        setSelectedView(storedViewId);
        return;
      }
    }

    const defaultView = views.find(view => view.isDefault);
    setSelectedView(defaultView ? defaultView.id : views[0].id);
  }, [views, selectedView, selectedIndex?.id]);

  const { mutate: mutateUpdatePrivateView } = useUpdateView();
  const { showToast } = useNotificationContext();

  const handleUpdateView = (updateViewRequest: UpdateViewRequest) => {
    mutateUpdatePrivateView(updateViewRequest, {
      onSuccess: data => {
        if (data) {
          showToast({
            type: 'success',
            message: translate('custom.notifications.views.update')
          });
        }
      }
    });
  };

  const handleSetSelectedView = (viewId: number | undefined) => {
    if (viewId !== undefined && selectedIndex?.id !== undefined) {
      storeViewSelection(selectedIndex.id, viewId);
    }
    setSelectedView(viewId);
  };

  const selectedViewData = selectedView && views ? views.find(view => view.id === selectedView) : undefined;

  const isRemoveViewMutation = useIsMutating({ mutationKey: mutateViewsKeys.deleteView }) > 0;

  return (
    <ViewsContext.Provider
      value={{
        views,
        selectedView,
        setSelectedView: handleSetSelectedView,
        selectedViewData,
        mutateViewLoading: isRemoveViewMutation,
        onUpdateView: handleUpdateView,
        hasUnsavedChanges,
        setHasUnsavedChanges,
        editingViewState,
        setEditingViewState
      }}
    >
      {children}
    </ViewsContext.Provider>
  );
};
